/**
 * The few in-world words (Canvas2D layer above the GL canvas), placed greedily by priority
 * so nothing overlaps: inbound threats (one label per target at its reticle, what is at stake and
 * when — `LYON L2 · 0:10`, a salvo `LYON L2 ×2 · 0:10`; the alert line names the rest and offers
 * the keys; an edge chevron, labelled `SILO T-0:11`, for each one off screen that puts something of
 * ours at stake — nothing else gets one), an ally's threats (a small teal timer), targets our side
 * marked (a reticle of four ticks on their capital, `MARKED · HALCYON`), the aim /
 * placement captions, braced cities, the front caption, our capital, a few of our biggest cities
 * (all of them only when zoomed in), the nations' names (big and spaced across each rival's and
 * ally's land when zoomed out; a small tag on the part of their land in view when zoomed in), and
 * last — culled first — launch hints of launches we do not track (a small `?` each; only the newest
 * says LAUNCH DETECTED · NO TRACK, briefly). Foreign cities
 * are only named when aimed at; a rival's capital carries its nation's callsign over the city's
 * name. Labels keep out of the HUD bands and blocks (toasts, the chip, menus) and the whole layer
 * hides once the match has a result.
 */
import { BALANCE, getGeo, landHexAt, TICK_HZ, type EntityView, type HexIdx, type PlayerId } from '@sa/sim';
import type { Camera } from '../input/camera';
import { SAME_TARGET_HEXES, clock } from '../state/alerts';
import { frontCaption } from '../state/front';
import { MARK_TICKS, type GameStore, type InboundThreat } from '../state/store';
import { allyStakeMatters, edgeCaption, threatGroups } from '../state/threats';
import { COLORS, FONT_STACK, cssRgb, type RGB } from '../theme';
import type { Palette } from './palette';
import type { SceneOut } from './scene';

/** the newest launch hint's words stay this long (s); every hint's `?` as long as its wedge */
const HINT_LABEL_S = 6;
const HINT_MARK_S = 20;
/** the ICBM / SLBM line under a timer only from this zoom (css px per mu) on a screen this tall */
const KIND_ZOOM = 20;
const KIND_MIN_H = 900;
/** HUD bands the world text keeps out of (css px): top bar, bottom build strip */
const SAFE_TOP = 76;
const SAFE_BOTTOM = 116;
/** chevrons and clamped captions stay this far inside the free area */
const EDGE = 16;
/** edge chevrons also keep clear of the share bar's notes (top) and the selection chip (bottom) */
const CHEVRON_TOP = SAFE_TOP + 40;
const CHEVRON_BOTTOM = SAFE_BOTTOM + 48;
/** off-screen chevrons closer than this (css px) merge into one */
const CHEVRON_MERGE_PX = 48;
/** a nation's big name / tag this close (css px) to its capital's label (which carries its callsign) is left out */
const NAME_NEAR_CAPITAL_PX = 120;
const PAD = 4;
/** own city names by zoom (css px per mu): L3+ below CITY_ZOOM, L2+ from it, every one from ALL_CITIES_ZOOM */
const CITY_ZOOM = 20;
const ALL_CITIES_ZOOM = 30;
/** below ALL_CITIES_ZOOM at most this many city names (besides the capital), biggest first */
const MAX_CITY_LABELS = 6;
/** our capital's level pips show this long before capitals are exposed (9:00) */
const CAPITAL_PIPS_LEAD_TICKS = 600;
/** a foreign city this close to the aim point (mu) is named */
const AIM_NAME_MU = 2.5;
/**
 * Nation names by zoom, relative to the whole-world zoom: big names across their land fully up to
 * BIG_FULL×, gone by BIG_GONE×; small tags on the land in view from TAG_FROM×, fully in by TAG_FULL×.
 */
const NATION_BIG_FULL = 1.5;
const NATION_BIG_GONE = 2.4;
const NATION_TAG_FROM = 1.9;
const NATION_TAG_FULL = 2.5;
/** a tag sits this deep in the nation's land (hex steps from its edge) when it can */
const TAG_DEPTH = 2;
/** arrows toward a nation's heartland, by octant (east first, clockwise on screen) */
const ARROWS = ['→', '↘', '↓', '↙', '←', '↖', '↑', '↗'];

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface TextStyle {
  col: RGB;
  a: number;
  size: number;
  weight?: number;
  spacing?: number;
  glow?: boolean;
  /** a dark outline under the letters (readable over the hatch patterns) */
  halo?: boolean;
}

const overlaps = (a: Rect, b: Rect) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

export class LabelLayer {
  private ctx: CanvasRenderingContext2D;
  private taken: Rect[] = [];
  /** unit glyphs on screen (words keep off them — except the faint nation names, which lie under) */
  private units: Rect[] = [];
  /** rival capital labels drawn this frame (callsign over the city), per nation: its name is said there */
  private capLabels = new Map<PlayerId, { x: number; y: number }[]>();
  /** the HUD's blocks this frame (toasts, chip, menus): edge chevrons slide off them */
  private hudBlocksNow: readonly Rect[] = [];
  private widths = new Map<string, number>();
  private w = 1;
  private h = 1;
  /** braced cities whose countdown a threat reticle already shows this frame */
  private bracedAt = new Set<number>();

  constructor(
    private canvas: HTMLCanvasElement,
    private names: ReadonlyMap<HexIdx, string>,
  ) {
    this.ctx = canvas.getContext('2d')!;
    // canvas text does not load web fonts by itself: ask, and re-measure once it is in
    void document.fonts?.load(`600 12px ${FONT_STACK}`).then(() => this.widths.clear());
  }

  resize(w: number, h: number, dpr: number): void {
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.widths.clear();
  }

  // ------------------------------------------------------------------ text primitives

  private font(s: TextStyle): string {
    return `${s.weight ?? 400} ${s.size}px ${FONT_STACK}`;
  }

  private measure(text: string, s: TextStyle): number {
    const key = `${this.font(s)}|${s.spacing ?? 2}|${text}`;
    let w = this.widths.get(key);
    if (w === undefined) {
      const c = this.ctx;
      c.font = this.font(s);
      c.letterSpacing = `${s.spacing ?? 2}px`;
      w = c.measureText(text).width;
      this.widths.set(key, w);
    }
    return w;
  }

  /** Draw left-aligned with the text box's top-left at (x, y). */
  private put(text: string, x: number, y: number, s: TextStyle): void {
    const c = this.ctx;
    c.font = this.font(s);
    c.letterSpacing = `${s.spacing ?? 2}px`;
    c.textAlign = 'left';
    c.textBaseline = 'top';
    if (s.halo) {
      c.shadowBlur = 0;
      c.lineJoin = 'round';
      c.lineWidth = 3.5;
      c.strokeStyle = `rgba(2, 6, 4, ${Math.min(0.9, s.a * 1.6).toFixed(3)})`;
      c.strokeText(text, x, y);
    }
    c.fillStyle = cssRgb(s.col, s.a);
    c.shadowColor = cssRgb(s.col, s.a * 0.6);
    c.shadowBlur = s.glow ? 6 : 0;
    c.fillText(text, x, y);
  }

  private free(r: Rect, units = true): boolean {
    if (r.x < 2 || r.y < 2 || r.x + r.w > this.w - 2 || r.y + r.h > this.h - 2) return false;
    for (const t of this.taken) if (overlaps(r, t)) return false;
    if (units) for (const t of this.units) if (overlaps(r, t)) return false;
    return true;
  }

  private take(r: Rect): void {
    this.taken.push({ x: r.x - PAD, y: r.y - PAD, w: r.w + 2 * PAD, h: r.h + 2 * PAD });
  }

  /**
   * A block of lines placed at the first free candidate (top-left corners for the block).
   * `force` draws at the first candidate even when everything collides (threats); `underUnits`
   * lets it lie over unit glyphs (only other words and the HUD keep it off).
   */
  private block(lines: { text: string; s: TextStyle }[], candidates: [number, number][], force = false, underUnits = false): Rect | null {
    const lh = lines.map((l) => Math.round(l.s.size * 1.25));
    const bw = Math.max(...lines.map((l) => this.measure(l.text, l.s)));
    const bh = lh.reduce((a, b) => a + b, 0);
    let at: Rect | null = null;
    for (const [x, y] of candidates) {
      const r = { x, y, w: bw, h: bh };
      if (this.free(r, !underUnits)) {
        at = r;
        break;
      }
    }
    if (!at && force && candidates.length) at = this.clampRect({ x: candidates[0][0], y: candidates[0][1], w: bw, h: bh });
    if (!at) return null;
    let y = at.y;
    lines.forEach((l, i) => {
      this.put(l.text, at.x, y, l.s);
      y += lh[i];
    });
    this.take(at);
    return at;
  }

  private clampRect(r: Rect): Rect {
    return {
      ...r,
      x: Math.min(this.w - EDGE - r.w, Math.max(EDGE, r.x)),
      y: Math.min(this.h - SAFE_BOTTOM - EDGE - r.h, Math.max(SAFE_TOP + EDGE, r.y)),
    };
  }

  /** The usual four spots around an anchor at (x, y) with a gap `g` (block size w × h). */
  private around(x: number, y: number, w: number, h: number, g: number): [number, number][] {
    return [
      [x + g, y - h - g * 0.4],
      [x + g, y + g * 0.4],
      [x - g - w, y - h - g * 0.4],
      [x - g - w, y + g * 0.4],
    ];
  }

  // ------------------------------------------------------------------ frame

  draw(cam: Camera, store: GameStore, pal: Palette, scene: SceneOut): void {
    const c = this.ctx;
    this.w = cam.vp.w;
    this.h = cam.vp.h;
    c.clearRect(0, 0, this.w, this.h);
    const v = store.view;
    if (v.result) return;
    this.taken = [
      { x: 0, y: 0, w: this.w, h: SAFE_TOP },
      { x: 0, y: this.h - SAFE_BOTTOM, w: this.w, h: SAFE_BOTTOM },
      // the HUD's blocks over the map (toasts, the chip, the nation menu): no word under them
      ...store.hudBlocks,
    ];
    this.hudBlocksNow = store.hudBlocks;
    this.capLabels.clear();
    // unit glyphs are not to be written over
    this.units = [];
    for (const p of scene.pos.values()) {
      const [sx, sy] = cam.worldToScreen(p.x, p.y);
      if (sx < -10 || sy < -10 || sx > this.w + 10 || sy > this.h + 10) continue;
      this.units.push({ x: sx - 8, y: sy - 8, w: 16, h: 16 });
    }

    const threats = store.inboundThreats ?? [];
    this.bracedAt.clear();
    this.threats(cam, store, threats);
    this.allyThreats(cam, store.allyThreats ?? []);
    this.marks(cam, store);
    this.aimCaption(cam, store);
    this.placementCaption(cam, store);
    this.cursorCue(cam, store);
    this.braced(cam, store);
    this.front(cam, store);
    this.cities(cam, store, pal, threats);
    this.nations(cam, store, pal);
    // launch hints last: the lowest priority, culled first when the threat area is busy
    if (!v.eliminated) this.hints(cam, scene);
  }

  /**
   * Targets our side marked (diplomacy): four corner ticks closing in and breathing on the nation's
   * capital and who marked it (`MARKED · HALCYON`; ours just `MARKED`), for MARK_TICKS. On screen only
   * (the alert line names an ally's mark).
   */
  private marks(cam: Camera, store: GameStore): void {
    const v = store.view;
    const c = this.ctx;
    for (const m of store.liveMarks(v.tick)) {
      const hex = store.capitalOf(m.target);
      if (hex < 0) continue;
      const [sx, sy] = cam.worldToScreen(store.world.cx[hex], store.world.cy[hex]);
      if (sx < 0 || sy < SAFE_TOP || sx > this.w || sy > this.h - SAFE_BOTTOM) continue;
      const col = m.from === v.me ? COLORS.me : COLORS.ally;
      const age = Math.max(0, Math.max(v.tick, store.frameTick) - m.tick) / TICK_HZ;
      const r = 14 + 10 * Math.max(0, 1 - age / 0.6) + 1.5 * Math.sin(age * 4);
      const a = 0.9 * Math.min(1, (MARK_TICKS / TICK_HZ - age) / 3);
      c.save();
      c.strokeStyle = cssRgb(col, a);
      c.lineWidth = 1.5;
      c.shadowColor = cssRgb(col, a * 0.6);
      c.shadowBlur = 6;
      c.beginPath();
      for (const [dx, dy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
        c.moveTo(sx + dx * r, sy + dy * (r - 6));
        c.lineTo(sx + dx * r, sy + dy * r);
        c.lineTo(sx + dx * (r - 6), sy + dy * r);
      }
      c.stroke();
      c.restore();
      this.taken.push({ x: sx - r, y: sy - r, w: 2 * r, h: 2 * r });
      const by = m.from === v.me ? null : v.players.find((p) => p.id === m.from)?.name.toUpperCase();
      const text = by ? `MARKED · ${by}` : 'MARKED';
      const s: TextStyle = { col, a, size: 11, weight: 600, spacing: 2 };
      const w = this.measure(text, s);
      this.block([{ text, s }], [[sx - w / 2, sy + r + 6], [sx - w / 2, sy - r - 20], ...this.around(sx, sy, w, 14, r + 4)]);
    }
  }

  /** Until our first push ever: "RMB · PUSH" beside the cursor over land we could push into. */
  private cursorCue(cam: Camera, store: GameStore): void {
    const cue = store.cursorCue;
    if (!cue) return;
    const [sx, sy] = cam.worldToScreen(cue.x, cue.y);
    const s: TextStyle = { col: COLORS.me, a: 0.85, size: 11, weight: 600, spacing: 2 };
    this.block([{ text: cue.text, s }], [[sx + 16, sy + 12], [sx + 16, sy - 26]], true);
  }

  // ------------------------------------------------------------------ front + brace

  /**
   * The push front's one readout (ADVANCING · STALLED · LOSING), small and dim, just outside the
   * middle of the land being pushed — the edge's animation carries the rest.
   */
  private front(cam: Camera, store: GameStore): void {
    const v = store.view;
    const cap = frontCaption(v.front);
    const at = store.frontAnchor;
    if (!at || v.eliminated || !cap) return;
    const [sx, sy] = cam.worldToScreen(at.x, at.y);
    if (sx < 0 || sy < SAFE_TOP || sx > this.w || sy > this.h - SAFE_BOTTOM) return;
    const { text, col, a } = cap;
    const s: TextStyle = { col, a: a * 0.55, size: 9, weight: 600, spacing: 2 };
    const w = this.measure(text, s);
    // on the outer side of the edge: away from our land, never across the glowing line
    const g = 14;
    const x = at.dx < -0.35 ? sx - g - w : at.dx > 0.35 ? sx + g : sx - w / 2;
    const y = at.dy < -0.35 ? sy - g - 12 : at.dy > 0.35 ? sy + g : sy - 6;
    this.block([{ text, s }], [[x, y], ...this.around(sx, sy, w, 12, g + 6)]);
  }

  /** Our braced cities: the time left under the shield ring. */
  private braced(cam: Camera, store: GameStore): void {
    const v = store.view;
    for (const e of v.entities.values()) {
      if (e.kind !== 'city' || e.owner !== v.me || e.state !== 'braced' || this.bracedAt.has(e.id)) continue;
      const until = e.detail?.braceUntil;
      if (until === undefined) continue;
      const [sx, sy] = cam.worldToScreen(e.x, e.y);
      if (sx < 0 || sy < SAFE_TOP || sx > this.w || sy > this.h - SAFE_BOTTOM) continue;
      const text = `BRACED ${clock((until - v.tick) / TICK_HZ)}`;
      const s: TextStyle = { col: COLORS.me, a: 0.9, size: 11, weight: 600, spacing: 2 };
      const w = this.measure(text, s);
      this.block([{ text, s }], [[sx - w / 2, sy + 17], ...this.around(sx, sy, w, 14, 16)]);
    }
  }

  // ------------------------------------------------------------------ threats

  /**
   * One label per target, at its reticle: what is at stake and when — `LYON L2 · 0:10`, a salvo
   * `LYON L2 ×2 · 0:10`, `CAPITAL CRITICAL · 0:08`; a city braced through the impact calm green
   * (`LYON L2 · BRACED · 0:10`); open ground a small amber `T-0:20`. No keys on the map (the alert
   * line offers them). The kind (ICBM / SLBM · whose) under it only zoomed in on a big screen.
   */
  private threats(cam: Camera, store: GameStore, threats: readonly InboundThreat[]): void {
    const soft = COLORS.dangerSoft;
    const groups = threatGroups(threats, SAME_TARGET_HEXES * Math.sqrt(3) * store.world.hexR);
    const kindLine = cam.zoom >= KIND_ZOOM && this.h >= KIND_MIN_H;
    /** off-screen targets with something of ours at stake: their edge chevrons, placed together after */
    const off: { px: number; py: number; hot: boolean; group: readonly InboundThreat[] }[] = [];
    groups.slice(0, 4).forEach((g, i) => {
      const t = g.find((x) => x.stake) ?? g[0];
      const lead = g[0];
      const staked = !!t.stake;
      const braced = staked && t.bracedSeconds !== null;
      // red only while something can be lost: a city braced through the impact is calm
      const hot = staked && !braced;
      const [tx, ty] = cam.worldToScreen(t.target.x, t.target.y);
      // (the store's onScreen uses the culling rect, which has a margin)
      if (tx < 0 || tx > this.w || ty < SAFE_TOP || ty > this.h - SAFE_BOTTOM) {
        if (edgeCaption(g)) off.push({ px: tx, py: ty, hot, group: g });
        return;
      }
      if (braced && t.cityId !== null) this.bracedAt.add(t.cityId);
      const n = g.length > 1 ? ` ×${g.length}` : '';
      const when = clock(lead.etaSeconds);
      const text = staked ? `${stakeShort(t)}${n} · ${braced ? 'BRACED · ' : ''}${when}` : `${n ? `${n.trim()} · ` : ''}T-${when}`;
      const col = braced ? COLORS.me : hot ? soft : COLORS.amber;
      const big = i === 0 && hot;
      const ts: TextStyle = { col, a: staked ? 1 : 0.8, size: big ? 18 : staked ? 14 : 11, weight: 700, spacing: 1, glow: hot };
      const lines = [{ text, s: ts }];
      if (kindLine && staked) lines.push({ text: `${lead.kind.toUpperCase()} · ${store.nameOf(lead.owner)}`, s: { col, a: 0.75, size: 11, weight: 400, spacing: 3, glow: false } });
      const w = Math.max(...lines.map((l) => this.measure(l.text, l.s)));
      const h = lines.reduce((a, l) => a + Math.round(l.s.size * 1.25), 0);
      // kept clear of the reticle (≥ 24 px from its centre), off unit glyphs if it can; a label on
      // something of ours (a capital ringed by SAMs) may lie over glyphs, and one at stake is always drawn
      const cand = [...this.around(tx, ty, w, h, 26), ...this.around(tx, ty, w, h, 44)];
      if (!this.block(lines, cand) && !(staked && this.block(lines, cand, false, true))) this.block(lines, cand, i === 0 || hot, true);
    });
    this.chevrons(off);
  }

  /**
   * The edge chevrons of off-screen threats, in the collision pass like every other word: each sits
   * where the bearing meets the free frame (below the top band, beside — never under — the toast
   * column and the other HUD blocks), and chevrons that would land within `CHEVRON_MERGE_PX` of
   * each other become one (`×3 · NEXT 0:03`).
   */
  private chevrons(off: readonly { px: number; py: number; hot: boolean; group: readonly InboundThreat[] }[]): void {
    const placed: { x: number; y: number; ang: number; hot: boolean; groups: (readonly InboundThreat[])[] }[] = [];
    for (const o of off) {
      const at = this.edgePoint(o.px, o.py);
      const near = placed.find((p) => Math.hypot(p.x - at.x, p.y - at.y) <= CHEVRON_MERGE_PX);
      if (near) {
        near.groups.push(o.group);
        near.hot ||= o.hot;
      } else placed.push({ ...at, hot: o.hot, groups: [o.group] });
    }
    for (const p of placed) {
      const all = p.groups.flat();
      const cap = p.groups.length === 1 ? edgeCaption(p.groups[0])! : `×${all.length} · NEXT ${clock(Math.min(...all.map((t) => t.etaSeconds)))}`;
      this.drawChevron(p.x, p.y, p.ang, p.hot ? COLORS.danger : COLORS.amber, 0.95, [cap]);
    }
  }

  /**
   * Where the line from the screen's middle toward (px, py) meets the chevrons' frame: inside the
   * edges, below the top band, above the bottom band — and off any HUD block (the toast column on
   * the right, a menu): a point that falls in one slides along the edge to just past it.
   */
  private edgePoint(px: number, py: number): { x: number; y: number; ang: number } {
    const x0 = EDGE;
    const y0 = CHEVRON_TOP;
    const x1 = this.w - EDGE;
    const y1 = this.h - CHEVRON_BOTTOM;
    const cx = (x0 + x1) / 2;
    const cy = (y0 + y1) / 2;
    const dx = px - cx;
    const dy = py - cy;
    const k = Math.min(dx !== 0 ? (dx > 0 ? x1 - cx : x0 - cx) / dx : Infinity, dy !== 0 ? (dy > 0 ? y1 - cy : y0 - cy) / dy : Infinity);
    let x = cx + dx * k;
    let y = cy + dy * k;
    for (const b of this.hudBlocksNow) {
      if (x < b.x - 24 || x > b.x + b.w + 24 || y < b.y - 24 || y > b.y + b.h + 24) continue;
      // on a side edge: slide below the block; on the top / bottom edge: slide beside it (toward the middle)
      if (x <= x0 + 1 || x >= x1 - 1) y = Math.min(y1, b.y + b.h + 28);
      else x = x > cx ? b.x - 28 : b.x + b.w + 28;
    }
    return { x, y, ang: Math.atan2(py - y, px - x) };
  }

  /**
   * An ally's city, capital or silo under a missile: a small teal timer by the impact point (never
   * at the head: mid-flight it may be over our own land). Their open ground keeps only its ring.
   */
  private allyThreats(cam: Camera, threats: readonly InboundThreat[]): void {
    for (const t of threats.filter(allyStakeMatters).slice(0, 3)) {
      const [tx, ty] = cam.worldToScreen(t.target.x, t.target.y);
      if (tx < 0 || tx > this.w || ty < SAFE_TOP || ty > this.h - SAFE_BOTTOM) continue;
      const s: TextStyle = { col: COLORS.ally, a: 0.85, size: 12, weight: 600, spacing: 1 };
      const text = `T-${clock(t.etaSeconds)}`;
      this.block([{ text, s }], this.around(tx, ty, this.measure(text, s), 15, 14));
    }
  }

  /** An edge chevron at (ex, ey) pointing along `ang`, with its caption (first line bold) on the inner side. */
  private drawChevron(ex: number, ey: number, ang: number, col: RGB, a: number, lines: string[]): void {
    const c = this.ctx;
    c.save();
    c.translate(ex, ey);
    c.rotate(ang);
    c.shadowColor = cssRgb(col, 0.6);
    c.shadowBlur = 8;
    c.strokeStyle = cssRgb(col, a);
    c.lineWidth = 2;
    for (const off of [0, -6]) {
      c.beginPath();
      c.moveTo(off - 7, -7);
      c.lineTo(off, 0);
      c.lineTo(off - 7, 7);
      c.stroke();
    }
    c.restore();
    if (!lines.length) return;
    // caption on the inner side of the chevron
    const ux = -Math.cos(ang);
    const uy = -Math.sin(ang);
    const styles: TextStyle[] = lines.map((_, i) => (i === 0 ? { col, a, size: 14, weight: 700, spacing: 1, glow: true } : { col, a: a * 0.8, size: 11, spacing: 2 }));
    const bw = Math.max(...lines.map((l, i) => this.measure(l, styles[i])));
    const bh = lines.length * 15;
    const bx = ex + ux * 22 + (ux < -0.3 ? -bw : ux > 0.3 ? 0 : -bw / 2);
    const by = ey + uy * 22 + (uy < -0.3 ? -bh : uy > 0.3 ? 0 : -bh / 2);
    // (the chevron itself is taken: no other word over it; its caption tries the inner side first)
    this.take({ x: ex - 10, y: ey - 10, w: 20, h: 20 });
    this.block(
      lines.map((text, i) => ({ text, s: styles[i] })),
      [[bx, by], [bx, by + (uy < 0 ? -bh - 8 : bh + 8)], [bx + (ux < 0 ? -24 : 24), by]],
      true,
      true,
    );
  }

  // ------------------------------------------------------------------ aim + placement

  private aimCaption(cam: Camera, store: GameStore): void {
    const aim = store.aim;
    if (!aim) return;
    const [sx, sy] = cam.worldToScreen(aim.x, aim.y);
    const lines: { text: string; s: TextStyle }[] = [];
    // what the click fires and when it lands: "ICBM ×2 · IMPACT 0:43", "RIPPLE ×8 · IMPACT 0:43"
    // (on a rival's land: whose — `ICBM ×2 · KESTREL · IMPACT 0:44`)
    const who = aim.zone === 'enemy' && aim.owner > 0 ? ` · ${store.nameOf(aim.owner)}` : '';
    const flight = (col: RGB, a: number) => ({ text: `${aim.salvo}${who} · IMPACT ${clock(aim.flightSeconds)}`, s: { col, a, size: 12, weight: 600, spacing: 1 } });
    const note = (text: string, a = 0.7) => lines.push({ text, s: { col: COLORS.neutral, a, size: 11 } });
    if (aim.blocked || aim.zone === 'ally') {
      lines.push({ text: 'ALLY · BLOCKED', s: { col: COLORS.danger, a: 0.95, size: 11, weight: 600 } });
    } else if (aim.reason) {
      note(aim.reason, 0.85);
    } else if (aim.zone === 'own') {
      lines.push({ text: 'OWN TERRITORY', s: { col: COLORS.danger, a: 0.95, size: 11, weight: 600 } });
      lines.push({ text: aim.confirmArmed ? 'CLICK AGAIN TO FIRE' : 'CLICK TWICE TO FIRE', s: { col: COLORS.dangerSoft, a: 0.8, size: 11 } });
      lines.push(flight(COLORS.dangerSoft, 0.85));
    } else {
      lines.push(flight(aim.zone === 'water' ? COLORS.me : COLORS.amber, 0.95));
      if (aim.fog) note('FOG · TARGET UNCONFIRMED');
    }
    // more loaded than one volley: Q again empties the magazines
    if (!aim.reason && !aim.blocked && !aim.ripple && aim.loaded > aim.ready) note(`Q AGAIN · RIPPLE ×${aim.loaded}`, 0.6);
    const w = Math.max(...lines.map((l) => this.measure(l.text, l.s)));
    this.block(lines, [[sx + 22, sy + 8], [sx + 22, sy - 8 - lines.length * 14], [sx - 22 - w, sy + 8], [sx - 22 - w, sy - 8 - lines.length * 14]], true);
  }

  private placementCaption(cam: Camera, store: GameStore): void {
    const pl = store.placement;
    if (!pl) return;
    const [sx, sy] = cam.worldToScreen(pl.x, pl.y);
    // an invalid site is not danger: dim grey, with the reason
    const lines: { text: string; s: TextStyle }[] = [{ text: pl.label, s: { col: pl.valid ? COLORS.me : COLORS.neutral, a: pl.valid ? 0.9 : 0.7, size: 11, weight: 600 } }];
    if (pl.reason) lines.push({ text: pl.reason, s: { col: COLORS.white, a: 0.8, size: 11, weight: 600 } });
    const w = Math.max(...lines.map((l) => this.measure(l.text, l.s)));
    this.block(lines, [[sx + 20, sy + 8], [sx + 20, sy - 8 - lines.length * 14], [sx - 20 - w, sy + 8]], true);
  }

  // ------------------------------------------------------------------ hints + cities

  /**
   * Launches we do not track: the wedge (scene) and a small `?` at each; only the newest says
   * LAUNCH DETECTED · NO TRACK, for its first few seconds — never a stack of the same words.
   */
  private hints(cam: Camera, scene: SceneOut): void {
    // off screen: nothing (an edge chevron is only for a threat to something of ours)
    const seen = scene.hints
      .filter((h) => h.age <= HINT_MARK_S)
      .map((h) => ({ h, at: cam.worldToScreen(h.x, h.y) }))
      .filter(({ at: [sx, sy] }) => sx > EDGE && sx < this.w - EDGE && sy > SAFE_TOP && sy < this.h - SAFE_BOTTOM);
    let newest: SceneOut['hints'][number] | null = null;
    for (const { h } of seen) if (h.age <= HINT_LABEL_S && (!newest || h.age < newest.age)) newest = h;
    for (const {
      h,
      at: [sx, sy],
    } of seen) {
      const a = 1 - h.age / HINT_MARK_S;
      const q: TextStyle = { col: COLORS.amber, a: 0.85 * a, size: 11, weight: 700 };
      const qw = this.measure('?', q);
      this.block([{ text: '?', s: q }], [[sx + 9, sy - 18], [sx - 9 - qw, sy - 18], [sx + 9, sy + 4]]);
      if (h !== newest) continue;
      const ta = 1 - h.age / HINT_LABEL_S;
      const s: TextStyle = { col: COLORS.amber, a: 0.85 * ta, size: 10, weight: 600, spacing: 1 };
      const text = 'LAUNCH DETECTED · NO TRACK';
      // (faint words: they may lie over unit glyphs, never over other words)
      this.block([{ text, s }], this.around(sx, sy, this.measure(text, s), 13, 20), false, true);
    }
  }

  /**
   * City names. Ours: the capital always; then the biggest few (L3+ zoomed out, L2+ from zoom 20,
   * at most six) and every one from zoom 30. A city a threat names is named at its reticle /
   * on the alert line instead. From 9:00 our capital's name turns green with its level as pips
   * (its hit points; red while critical), and from 10:00 rival capitals are named in their colour
   * wherever they are — a critical one with its countdown (`CRITICAL 1:12`).
   */
  private cities(cam: Camera, store: GameStore, pal: Palette, threats: readonly InboundThreat[]): void {
    const v = store.view;
    const geo = getGeo(store.world);
    const z = cam.zoom;
    const capHex = store.capitalHex;
    const aim = store.aim;
    const near = (x: number, y: number, r: number) => (e: { x: number; y: number }) => (e.x - x) ** 2 + (e.y - y) ** 2 < r * r;
    const threatened = new Set(threats.map((t) => t.label));
    const minLevel = z >= ALL_CITIES_ZOOM ? 0 : z >= CITY_ZOOM ? 2 : 3;
    const list: { e: EntityView; name: string; rank: number; sx: number; sy: number }[] = [];
    let own = 0;
    const ownCities: { e: EntityView; name: string; level: number; sx: number; sy: number }[] = [];
    for (const e of v.entities.values()) {
      if (e.kind !== 'city') continue;
      const [sx, sy] = cam.worldToScreen(e.x, e.y);
      if (sx < -20 || sy < -20 || sx > this.w + 20 || sy > this.h + 20) continue;
      const hex = landHexAt(geo, e.x, e.y);
      const name = this.names.get(hex);
      if (!name) continue;
      // a threatened city is named at its reticle (or on the alert line) instead
      if (threatened.has(name) || threats.some((t) => t.cityId === e.id || near(t.target.x, t.target.y, 1.5)(e))) continue;
      const level = e.detail?.level ?? e.extra;
      const isCap = hex === capHex && e.owner === v.me;
      if (isCap) list.push({ e, name, rank: 100, sx, sy });
      else if (v.capitalsExposed && e.owner !== 0 && !pal.isFriendly(e.owner) && hex === store.capitalOf(e.owner)) list.push({ e, name, rank: 40, sx, sy });
      else if (e.owner === v.me) {
        if (level >= minLevel) ownCities.push({ e, name, level, sx, sy });
      } else if (aim && near(aim.x, aim.y, AIM_NAME_MU)(e)) list.push({ e, name, rank: 50, sx, sy });
    }
    // the biggest of our cities first; zoomed out only a handful
    ownCities.sort((a, b) => b.level - a.level || a.name.localeCompare(b.name));
    for (const c of ownCities) {
      if (z < ALL_CITIES_ZOOM && own >= MAX_CITY_LABELS) break;
      own++;
      list.push({ e: c.e, name: c.name, rank: 10 + c.level, sx: c.sx, sy: c.sy });
    }
    // rival capitals from 10:00 are targets: named at their (public) spot even when unseen
    if (v.capitalsExposed) {
      const out = store.eliminated;
      for (const p of v.players) {
        if (pal.isFriendly(p.id) || out.has(p.id)) continue;
        const hex = store.capitalOf(p.id);
        const name = hex >= 0 ? this.names.get(hex) : undefined;
        if (!name || list.some((l) => l.rank === 40 && landHexAt(geo, l.e.x, l.e.y) === hex)) continue;
        const [sx, sy] = cam.worldToScreen(store.world.cx[hex], store.world.cy[hex]);
        if (sx < -20 || sy < -20 || sx > this.w + 20 || sy > this.h + 20) continue;
        list.push({ e: { owner: p.id, x: store.world.cx[hex], y: store.world.cy[hex] } as EntityView, name, rank: 40, sx, sy });
      }
    }
    list.sort((a, b) => b.rank - a.rank);
    // our capital's hit points show from a minute before capitals are exposed (rivals stock up for it)
    const hp = v.tick >= BALANCE.phases.capitalsExposedTick - CAPITAL_PIPS_LEAD_TICKS;
    for (const { e, name, rank, sx, sy } of list) {
      const isCap = rank === 100;
      const exposed = isCap && hp;
      // a critical capital (§1.11): ours goes red, a rival's carries its countdown (a target of opportunity)
      const left = isCap ? store.criticalLeft(v.me, v.tick) : rank === 40 ? store.criticalLeft(e.owner, v.tick) : null;
      const s: TextStyle = isCap
        ? { col: left !== null ? COLORS.danger : exposed ? COLORS.me : COLORS.white, a: 0.9, size: 11, weight: 600, spacing: 3 }
        : { col: pal.color(e.owner), a: rank >= 40 ? 0.85 : 0.6, size: 11, weight: rank === 40 ? 600 : 400, spacing: 2 };
      // a rival's capital: its nation's callsign (bold, its colour) over the city's name — the
      // alert line's `VULCAN CAPITAL CRITICAL` is the same word as the map's
      const lines =
        rank === 40
          ? [
              { text: store.nameOf(e.owner), s: { ...s, a: 0.95, size: 12, weight: 700, halo: true } },
              { text: name, s: { ...s, a: 0.7, size: 10, weight: 400, halo: true } },
            ]
          : [{ text: name, s }];
      // (Q: our launchers reach it — the alert line's Q STRIKE)
      const strike = store.strikeOffer?.player === e.owner ? ' · L' : '';
      if (!isCap && left !== null) lines.push({ text: `CRITICAL ${clock(left / TICK_HZ)}${strike}`, s: { ...s, a: 0.95, weight: 700 } });
      const w = Math.max(...lines.map((l) => this.measure(l.text, l.s)));
      const bh = lines.reduce((a, l) => a + Math.round(l.s.size * 1.25), 0);
      const at = this.block(lines, this.around(sx, sy, w, exposed ? 20 : bh, rank === 40 || exposed ? 22 : 10), isCap);
      if (at && exposed) this.pips(at.x, at.y + 15, e.detail?.level ?? e.extra, s.col);
      if (at && rank === 40) this.capLabels.set(e.owner, [...(this.capLabels.get(e.owner) ?? []), { x: at.x + at.w / 2, y: at.y + at.h / 2 }]);
    }
  }

  /**
   * Every other nation's name on its land (ours needs none): zoomed out, one big spaced name in its
   * colour across the deepest point of its largest landmass (`store.nationPlaces`), sized to that
   * land and faint enough not to fight the units; zoomed in, a small tag on the part of its land in
   * view nearest its heartland, with an arrow toward it when that is off screen. Last in the
   * collision pass: everything else keeps its place, a name that does not fit waits.
   */
  private nations(cam: Camera, store: GameStore, pal: Palette): void {
    const v = store.view;
    const rel = cam.zoom / cam.minZoom;
    const big = Math.min(1, Math.max(0, (NATION_BIG_GONE - rel) / (NATION_BIG_GONE - NATION_BIG_FULL)));
    const small = Math.min(1, Math.max(0, (rel - NATION_TAG_FROM) / (NATION_TAG_FULL - NATION_TAG_FROM)));
    if (big <= 0 && small <= 0) return;
    const w = store.world;
    const { anchors, depth } = store.nationPlaces(v.tick);
    const step = Math.sqrt(3) * w.hexR;
    const inside = (sx: number, sy: number, m = 0) => sx > m && sx < this.w - m && sy > SAFE_TOP + m && sy < this.h - SAFE_BOTTOM - m;
    // its capital's label already says its callsign close by: no second name beside it
    const saidNear = (p: PlayerId, sx: number, sy: number) => (this.capLabels.get(p) ?? []).some((c) => Math.hypot(c.x - sx, c.y - sy) <= NAME_NEAR_CAPITAL_PX);
    const named = new Set<PlayerId>();
    if (big > 0) {
      for (const a of [...anchors.values()].sort((p, q) => q.area - p.area)) {
        if (a.player === v.me) continue;
        const [sx, sy] = cam.worldToScreen(a.x, a.y);
        if (!inside(sx, sy)) continue;
        if (saidNear(a.player, sx, sy)) {
          named.add(a.player);
          continue;
        }
        const name = store.nameOf(a.player);
        // as tall as the land is deep (its inland radius on screen), within reason
        const size = Math.round(Math.min(26, Math.max(10, a.depth * step * cam.zoom * 0.5)));
        const st: TextStyle = { col: pal.color(a.player), a: 0.6 * big, size, weight: 700, spacing: Math.round(size * 0.4), halo: true };
        const tw = this.measure(name, st);
        const h = Math.round(size * 1.25);
        // (faint, under the units: only other words and the HUD keep it off)
        if (this.block([{ text: name, s: st }], [[sx - tw / 2, sy - h / 2], [sx - tw / 2, sy + 6], [sx - tw / 2, sy - h - 6], [sx - tw / 2, sy + h + 12]], false, true)) named.add(a.player);
      }
    }
    if (small <= 0) return;
    // the tag spot per nation: its land in view (deep enough, else any) nearest its heartland
    const [x0, y0] = cam.screenToWorld(24, SAFE_TOP + 24);
    const [x1, y1] = cam.screenToWorld(this.w - 24, this.h - SAFE_BOTTOM - 24);
    const spot = new Map<PlayerId, { hex: number; d: number; deep: boolean }>();
    for (let hx = 0; hx < w.landCount; hx++) {
      const o = v.owner[hx];
      if (o === 0 || o === v.me || named.has(o)) continue;
      const x = w.cx[hx];
      const y = w.cy[hx];
      if (x < x0 || x > x1 || y < y0 || y > y1) continue;
      const a = anchors.get(o);
      if (!a) continue;
      const deep = depth[hx] >= TAG_DEPTH;
      const d = (x - a.x) ** 2 + (y - a.y) ** 2;
      const was = spot.get(o);
      if (!was || (deep && !was.deep) || (deep === was.deep && d < was.d)) spot.set(o, { hex: hx, d, deep });
    }
    for (const [o, at] of spot) {
      const a = anchors.get(o)!;
      const [sx, sy] = cam.worldToScreen(w.cx[at.hex], w.cy[at.hex]);
      if (saidNear(o, sx, sy)) continue;
      const [ax, ay] = cam.worldToScreen(a.x, a.y);
      // its heartland off screen: point at it
      const arrow = inside(ax, ay) ? '' : ` ${ARROWS[Math.round(((Math.atan2(ay - sy, ax - sx) + 2 * Math.PI) % (2 * Math.PI)) / (Math.PI / 4)) % 8]}`;
      const text = `${store.nameOf(o)}${arrow}`;
      const st: TextStyle = { col: pal.color(o), a: 0.7 * small, size: 11, weight: 600, spacing: 3, halo: true };
      const tw = this.measure(text, st);
      this.block([{ text, s: st }], [[sx - tw / 2, sy - 7], ...this.around(sx, sy, tw, 14, 10)], false, true);
    }
  }

  /** A capital's level as small squares under its name (capital levels are its hit points). */
  private pips(x: number, y: number, n: number, col: RGB): void {
    const c = this.ctx;
    c.shadowBlur = 0;
    c.fillStyle = cssRgb(col, 0.85);
    for (let i = 0; i < n; i++) c.fillRect(x + i * 7, y, 4, 4);
  }
}

/**
 * A threat's stake in a few words for its label: a city by name and level (`LYON L2`; `CITY L2`
 * when the impact is not at a named city), `CAPITAL` / `CAPITAL CRITICAL`, `SILO`, `ALLY SAM`.
 */
export function stakeShort(t: Pick<InboundThreat, 'label' | 'stake'>): string {
  const stake = t.stake ?? '';
  const lvl = /^CITY (L\d)$/.exec(stake);
  if (lvl && !/ KM |^NEAR /.test(t.label)) return `${t.label} ${lvl[1]}`;
  return stake.replace(' · ', ' ');
}
